import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "../../components/header";
import Footer from "../../components/footer";
import SeoHead from "../../components/seoHead";
import { useAuth } from "../../contexts/AuthContext";
import { useLocalePath, useAppLocale } from "../../i18n/LocaleRouting";
import {
  SITE_NAME,
  SITE_URL,
  absoluteLocalizedUrl,
} from "../../i18n/seoUrls";
import { isFirebaseConfigured } from "../../lib/firebase";
import {
  COMMUNITY_MAX_TEXT,
  addComment,
  createPost,
  fetchComments,
  formatRelativeTime,
  subscribePosts,
  toggleLike,
  type CommunityComment,
  type CommunityPost,
} from "../../services/communityService";
import { SEED_COMMUNITY_POSTS } from "./seedData";

function Avatar({
  name,
  photoURL,
  size = 40,
}: {
  name: string;
  photoURL: string | null;
  size?: number;
}) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover shrink-0 bg-gray-200"
        style={{ width: size, height: size }}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#F05A2A] text-white font-bold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      aria-hidden
    >
      {initial}
    </div>
  );
}

function ImageGrid({ urls }: { urls: string[] }) {
  if (!urls.length) return null;
  if (urls.length === 1) {
    return (
      <img
        src={urls[0]}
        alt=""
        className="mt-3 w-full max-h-[360px] object-cover rounded-xl"
        loading="lazy"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
    );
  }
  return (
    <div
      className={`mt-3 grid gap-1.5 ${
        urls.length === 2 ? "grid-cols-2" : "grid-cols-2"
      }`}
    >
      {urls.slice(0, 4).map((url) => (
        <img
          key={url}
          src={url}
          alt=""
          className="w-full h-36 md:h-44 object-cover rounded-lg"
          loading="lazy"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
      ))}
    </div>
  );
}

const Community: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localePath = useLocalePath();
  const locale = useAppLocale();
  const communityUrl = absoluteLocalizedUrl("/community", locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const photosComingSoon = t("community.photosComingSoon");
  const { user, loading: authLoading } = useAuth();

  const [remotePosts, setRemotePosts] = useState<CommunityPost[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState<string | null>(null);

  const [composeText, setComposeText] = useState("");
  const [posting, setPosting] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [photoFeatureTip, setPhotoFeatureTip] = useState<string | null>(null);

  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [commentsByPost, setCommentsByPost] = useState<
    Record<string, CommunityComment[]>
  >({});
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {},
  );
  const [commentLoading, setCommentLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setFeedLoading(true);
    const unsub = subscribePosts(
      (posts) => {
        setRemotePosts(posts);
        setFeedLoading(false);
        setFeedError(null);
      },
      (err) => {
        setFeedError(err.message);
        setFeedLoading(false);
      },
    );
    return unsub;
  }, []);

  const posts = useMemo(() => {
    if (remotePosts.length > 0) return remotePosts;
    if (feedLoading) return [];
    return SEED_COMMUNITY_POSTS;
  }, [remotePosts, feedLoading]);

  const showingSeeds = remotePosts.length === 0 && !feedLoading;

  const requireLogin = () => {
    navigate(localePath("/auth"), {
      state: { from: localePath("/community") },
    });
  };

  const showPhotosComingSoon = () => {
    setComposeError(null);
    setPhotoFeatureTip(photosComingSoon);
  };

  const handleCreatePost = async () => {
    if (!user) {
      requireLogin();
      return;
    }
    if (!isFirebaseConfigured) {
      setComposeError("Firebase is not configured yet.");
      return;
    }
    setPosting(true);
    setComposeError(null);
    setPhotoFeatureTip(null);
    setActionError(null);
    try {
      await createPost({ user, text: composeText, files: [] });
      setComposeText("");
    } catch (err) {
      setComposeError(
        err instanceof Error ? err.message : "Failed to publish post.",
      );
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (post: CommunityPost) => {
    if (post.isSeed) {
      setActionError("These are sample posts. Sign in and create your own!");
      return;
    }
    if (!user) {
      requireLogin();
      return;
    }
    const liked = post.likedBy.includes(user.uid);
    try {
      await toggleLike(post.id, user.uid, liked);
      setActionError(null);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to update like.",
      );
    }
  };

  const toggleComments = async (post: CommunityPost) => {
    if (expandedPostId === post.id) {
      setExpandedPostId(null);
      return;
    }
    setExpandedPostId(post.id);
    if (post.isSeed) {
      setCommentsByPost((prev) => ({
        ...prev,
        [post.id]: [
          {
            id: "seed-c1",
            authorId: "seed",
            authorName: "ShopHopper",
            authorPhotoURL: null,
            text: "Nice pull! Which store was this?",
            createdAt: new Date(Date.now() - 1000 * 60 * 20),
          },
        ],
      }));
      return;
    }
    if (commentsByPost[post.id]) return;
    setCommentLoading(post.id);
    try {
      const list = await fetchComments(post.id);
      setCommentsByPost((prev) => ({ ...prev, [post.id]: list }));
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to load comments.",
      );
    } finally {
      setCommentLoading(null);
    }
  };

  const handleComment = async (post: CommunityPost) => {
    if (post.isSeed) {
      setActionError("These are sample posts. Sign in and create your own!");
      return;
    }
    if (!user) {
      requireLogin();
      return;
    }
    const text = (commentDrafts[post.id] ?? "").trim();
    if (!text) return;
    setCommentLoading(post.id);
    try {
      await addComment({ postId: post.id, user, text });
      const list = await fetchComments(post.id);
      setCommentsByPost((prev) => ({ ...prev, [post.id]: list }));
      setCommentDrafts((prev) => ({ ...prev, [post.id]: "" }));
      setActionError(null);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Failed to post comment.",
      );
    } finally {
      setCommentLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f5]">
      <SeoHead
        title={t("seo.community.title")}
        description={t("seo.community.description")}
        path="/community"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t("seo.community.title"),
            description: t("seo.community.description"),
            url: communityUrl,
            isPartOf: {
              "@type": "WebSite",
              name: SITE_NAME,
              url: SITE_URL + "/",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("common.home"),
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("community.title"),
                item: communityUrl,
              },
            ],
          },
        ]}
      />
      <div className="flex-1">
        <Header />

        <main className="w-full max-w-[640px] mx-auto px-4 py-6 md:py-10">
          <div className="mb-5">
            <h1 className="text-[24px] md:text-[28px] font-bold text-[#111]">
              {t("community.title")}
            </h1>
            <p className="mt-1 text-sm text-[#666]">
              {t("community.subtitle")}
            </p>
          </div>

          {!isFirebaseConfigured && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Firebase is not configured in this environment. Showing sample
              posts. Add your Firebase keys and enable Firestore + Storage to go
              live.
            </div>
          )}

          {showingSeeds && isFirebaseConfigured && (
            <div className="mb-4 rounded-xl border border-[#e5e5e5] bg-white px-4 py-3 text-sm text-[#666]">
              No live posts yet — showing samples. Be the first to share!
            </div>
          )}

          {/* Composer */}
          <section className="mb-5 rounded-xl border border-[#e5e5e5] bg-white p-4 shadow-sm">
            <div className="flex gap-3">
              <Avatar
                name={user?.displayName || user?.email || "You"}
                photoURL={user?.photoURL ?? null}
              />
              <div className="flex-1 min-w-0">
                <textarea
                  value={composeText}
                  onChange={(e) => setComposeText(e.target.value)}
                  onFocus={() => {
                    if (!authLoading && !user) requireLogin();
                  }}
                  placeholder={
                    user
                      ? t("community.placeholderSignedIn")
                      : t("community.placeholderGuest")
                  }
                  rows={3}
                  maxLength={COMMUNITY_MAX_TEXT}
                  className="w-full resize-none rounded-lg border border-[#e5e5e5] bg-[#fafafa] px-3 py-2 text-sm text-[#111] outline-none focus:border-[#F05A2A] focus:ring-1 focus:ring-[#F05A2A]"
                />

                {composeError && (
                  <p className="mt-2 text-xs text-red-600">{composeError}</p>
                )}
                {photoFeatureTip && (
                  <p className="mt-2 text-xs text-[#888]">{photoFeatureTip}</p>
                )}

                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!user) {
                          requireLogin();
                          return;
                        }
                        showPhotosComingSoon();
                      }}
                      className="rounded-lg border border-[#e5e5e5] px-3 py-1.5 text-xs font-medium text-[#444] hover:bg-[#f5f5f5]"
                    >
                      {t("community.addPhotos")}
                    </button>
                    <span className="text-[11px] text-[#999]">
                      {t("common.comingSoon")}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={posting}
                    onClick={handleCreatePost}
                    className="rounded-lg bg-[#F05A2A] px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {posting
                      ? t("community.posting")
                      : user
                        ? t("community.post")
                        : t("community.signInToPost")}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {actionError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          {feedError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Could not load live posts: {feedError}
            </div>
          )}

          {feedLoading ? (
            <div className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-10 text-center text-sm text-[#888]">
              Loading feed...
            </div>
          ) : (
            <ul className="flex flex-col gap-4">
              {posts.map((post) => {
                const liked = Boolean(user && post.likedBy.includes(user.uid));
                const comments = commentsByPost[post.id] ?? [];
                const expanded = expandedPostId === post.id;

                return (
                  <li
                    key={post.id}
                    className="rounded-xl border border-[#e5e5e5] bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar
                        name={post.authorName}
                        photoURL={post.authorPhotoURL}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-[#111] text-sm">
                            {post.authorName}
                          </span>
                          <span className="text-xs text-[#999]">
                            {formatRelativeTime(post.createdAt)}
                          </span>
                          {post.isSeed && (
                            <span className="rounded border border-[#ddd] bg-[#f3f3f3] px-1.5 py-0.5 text-[10px] text-[#666]">
                              Sample
                            </span>
                          )}
                        </div>
                        {post.text ? (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-[#222] leading-relaxed">
                            {post.text}
                          </p>
                        ) : null}
                        <ImageGrid urls={post.imageUrls} />

                        <div className="mt-3 flex items-center gap-4 text-sm">
                          <button
                            type="button"
                            onClick={() => handleLike(post)}
                            className={`font-medium ${
                              liked ? "text-[#F05A2A]" : "text-[#555]"
                            }`}
                          >
                            {liked ? "♥" : "♡"} {post.likeCount}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleComments(post)}
                            className="font-medium text-[#555]"
                          >
                            💬 {post.commentCount}
                          </button>
                        </div>

                        {expanded && (
                          <div className="mt-3 border-t border-[#eee] pt-3">
                            {commentLoading === post.id && !comments.length ? (
                              <p className="text-xs text-[#888]">
                                Loading comments...
                              </p>
                            ) : (
                              <ul className="flex flex-col gap-2.5 mb-3">
                                {comments.map((c) => (
                                  <li key={c.id} className="flex gap-2">
                                    <Avatar
                                      name={c.authorName}
                                      photoURL={c.authorPhotoURL}
                                      size={28}
                                    />
                                    <div className="rounded-lg bg-[#f5f5f5] px-2.5 py-1.5 flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-[#111]">
                                          {c.authorName}
                                        </span>
                                        <span className="text-[10px] text-[#999]">
                                          {formatRelativeTime(c.createdAt)}
                                        </span>
                                      </div>
                                      <p className="text-xs text-[#333] mt-0.5 whitespace-pre-wrap">
                                        {c.text}
                                      </p>
                                    </div>
                                  </li>
                                ))}
                                {!comments.length && (
                                  <p className="text-xs text-[#888]">
                                    No comments yet.
                                  </p>
                                )}
                              </ul>
                            )}

                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={commentDrafts[post.id] ?? ""}
                                onChange={(e) =>
                                  setCommentDrafts((prev) => ({
                                    ...prev,
                                    [post.id]: e.target.value,
                                  }))
                                }
                                onFocus={() => {
                                  if (!user) requireLogin();
                                }}
                                placeholder={
                                  user
                                    ? "Write a comment..."
                                    : "Sign in to comment..."
                                }
                                maxLength={500}
                                className="flex-1 rounded-lg border border-[#e5e5e5] bg-[#fafafa] px-3 py-1.5 text-xs outline-none focus:border-[#F05A2A]"
                              />
                              <button
                                type="button"
                                disabled={commentLoading === post.id}
                                onClick={() => handleComment(post)}
                                className="rounded-lg bg-[#111] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                              >
                                Send
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Community;
